import { createContainer } from "unstated-next"
import AnalyticsRequestFilter from "../models/analytics/AnalyticsRequestFilter";
import { useEffect, useState, useMemo } from "react";
import * as voicifyApi from '../api';
import SessionDateModel from "../models/analytics/api/SessionDateModel";
import ConversationalSuccessEvent from "../models/analytics/ConversationalSuccessEvent";
import { TAGS } from "../constants/analyticsConstants/generativeCallReview";

// ========== TYPE DEFINITIONS ==========

/**
 * Per-app breakdown of appointments
 */
interface AppointmentByApp {
    appId: string;
    appName: string;
    scheduled: number;
    rescheduled: number;
    cancelled: number;
    checkedNextAppt: number;
}

/**
 * Per-app breakdown of new patient bookings
 */
interface NewPatientBookingsByApp {
    appId: string;
    appName: string;
    newPatient: number;
    existingPatient: number;
}

/**
 * Per-app breakdown of contained calls
 */
interface ContainedCallsByApp {
    appId: string;
    appName: string;
    scheduled: number;
    scheduledEnglish: number;
    scheduledSpanish: number;
    rescheduled: number;
    rescheduledEnglish: number;
    rescheduledSpanish: number;
    cancelled: number;
    cancelledEnglish: number;
    cancelledSpanish: number;
    checkedNextAppt: number;
    checkedNextApptEnglish: number;
    checkedNextApptSpanish: number;
    faq: number;
    faqEnglish: number;
    faqSpanish: number;
    transfer: number;
    transferEnglish: number;
    transferSpanish: number;
}

/**
 * Average call length broken down by appointment category
 */
interface AverageCallLengthByCategory {
    scheduled: number;
    rescheduled: number;
    cancelled: number;
    checkedNextAppt: number;
}

/**
 * Call volume by app for a specific hour
 */
interface CallVolumeByApp {
    appName: string;
    count: number;
}

/**
 * Call volume aggregated by hour with per-app breakdown
 */
interface CallVolumeByHour {
    hour: string;
    calls: number;
    apps: CallVolumeByApp[];
}

/**
 * Healthcare metrics for a single application
 * TODO: Consider adding API endpoint for app-level metrics
 */
interface HealthcareAppMetrics {
    scheduled: number;
    scheduledEnglish: number;
    scheduledSpanish: number;
    rescheduled: number;
    rescheduledEnglish: number;
    rescheduledSpanish: number;
    cancelled: number;
    cancelledEnglish: number;
    cancelledSpanish: number;
    checkedNextAppt: number;
    checkedNextApptEnglish: number;
    checkedNextApptSpanish: number;
    totalAppointmentActions: number;
    containedCallsCount: number;
    containedCallsPercentage: number | string;
    hasSpanishCalls: boolean;
}

/**
 * Healthcare metrics aggregated across multiple applications (organization-level)
 * TODO: Add API endpoint: GET /api/analytics/org/{orgId}/healthcare/metrics
 */
interface HealthcareOrgMetrics extends HealthcareAppMetrics {
    totalEngagements: number;
    offHoursCalls: number;
    transferCount: number;
    faqCount: number;
    averageCallLength: number;
    averageContainedCallLength: number;
    averageCallLengthByCategory: AverageCallLengthByCategory;
    laborHoursSaved: number;
    callVolumeByHour: CallVolumeByHour[];
    appointmentsByApp: AppointmentByApp[];
    containedCallsByApp: ContainedCallsByApp[];
    newPatientBookings: number;
    existingPatientBookings: number;
    totalBookings: number;
    newPatientBookingsByApp: NewPatientBookingsByApp[];
}

/**
 * Food ordering metrics for a single application
 * TODO: Consider adding API endpoint for app-level metrics
 */
interface FoodOrderingAppMetrics {
    totalSold: number;
    averageOrderLength: number;
    convertedCustomers: number;
    repeatCustomers: number;
    uniqueEngagementCount: number;
    upsellPercentage: number;
}

/**
 * Food ordering metrics aggregated across multiple applications
 * TODO: Add API endpoint: GET /api/analytics/org/{orgId}/ordering/metrics
 */
interface FoodOrderingOrgMetrics extends FoodOrderingAppMetrics {
    // Future: Add org-specific aggregations
}

/**
 * Labor savings calculation results
 */
interface LaborSavingsMetrics {
    hoursSaved: number;
    totalDurationSeconds: number;
}

function useTelephonyAnalyticsContainer() {
    const [loading, setLoading] = useState(true);
    const [loadingSessionId, setLoadingSessionId] = useState("");
    const [errors, setErrors] = useState([]);
    const [itemAddedToOrderEvents, setItemAddedToOrderEvents] = useState([]);
    const [callTransferredEvents, setCallTransferredEvents] = useState([]);
    const [orderPlacedEvents, setOrderPlacedEvents] = useState([]);
    const [unsuccessfulOrderSessionIds, setFailedOrderSessionIds] = useState([]);
    const [ordersPlacedSessionIds, setOrdersPlacedSessionIds] = useState([]);
    const [orderTransferredSessionIds, setOrderTransferredSessionIds] = useState([]);
    const [noOrderSessionIds, setNoOrderSessionIds] = useState([]);
    const [allEventsCache, setAllEventsCache] = useState({});
    const [immediateTransferEvents, setImmediateTransferEvents] = useState([]);
    const [conversationalSuccessEvents, setConversationalSuccessEvents] = useState<ConversationalSuccessEvent[]>([]);
    const [callCompleteEvents, setCallCompleteEvents] = useState([]);
    const [initialSessions, setInitialSessions] = useState([] as SessionDateModel[]);
    const [sessions, setSessions] = useState([] as SessionDateModel[]);
    const [orderAttempts, setOrderAttempts] = useState(0);
    const [ordersPlaced, setOrdersPlaced] = useState(0);
    const [transfers, setTransfers] = useState(0);
    const [orderPlacedRate, setOrderPlacedRate] = useState(0);
    const [orderTransferRate, setOrderTransferRate] = useState(0);
    const [orderTransfers, setOrderTransfers] = useState<number>(null);
    const [transferRate, setTransferRate] = useState(0);
    const [immediateTransfers, setImmediateTransfers] = useState(0);
    const [immediateTransferRate, setImmediateTransferRate] = useState(0);
    const [conversationalSuccessRate, setConversationalSuccessRate] = useState(0);
    const [attemptedOrdersConversationalSuccessRate, setAttemptedOrdersConversationalSuccessRate] = useState(0);
    const [attemptedOrderConversationalSuccess, setAttemptedOrderConversationalSuccess] = useState(0);
    const [conversationalSuccess, setConversationalSuccess] = useState(0);

    useEffect(() => {
        if (loading) {
            return;
        }
        let itemAddedToOrderSessionIds = getUniqueSessionIds(itemAddedToOrderEvents);
        itemAddedToOrderSessionIds = itemAddedToOrderSessionIds.filter(x => sessions.some(s => s.sessionId === x));
        const orderAttempts = itemAddedToOrderSessionIds.length;
        setOrderAttempts(orderAttempts);

        let placedSessionIds = getUniqueSessionIds(orderPlacedEvents);
        placedSessionIds = placedSessionIds.filter(x => sessions.some(s => s.sessionId === x));

        const ordersPlaced = placedSessionIds.length;
        setOrdersPlaced(ordersPlaced);
        setOrdersPlacedSessionIds(placedSessionIds);

        let failOrderSessionIds = itemAddedToOrderSessionIds.filter(x => !placedSessionIds.includes(x));
        setFailedOrderSessionIds(failOrderSessionIds);

        const noOrderSessions = sessions.filter(x => !itemAddedToOrderSessionIds.includes(x.sessionId));
        setNoOrderSessionIds(noOrderSessions.map(s => s.sessionId));

        const callTransferedSessionIds = getUniqueSessionIds(callTransferredEvents);
        setTransfers(callTransferedSessionIds.length);

        setTransferRate(callTransferedSessionIds.length / sessions.length);

        // union of call transferred and order attempted session ids
        const oTransferSessIds = callTransferedSessionIds.filter(x => itemAddedToOrderSessionIds.includes(x));
        setOrderTransferredSessionIds(oTransferSessIds);

        const immediateTransferSessionIds = getUniqueSessionIds(immediateTransferEvents);
        setImmediateTransferRate(immediateTransferSessionIds.length / sessions.length);
        setImmediateTransfers(immediateTransferSessionIds.length);

        // get call transfers that happened in a session where items were added to order
        if (!itemAddedToOrderSessionIds?.length) {
            setOrderPlacedRate(-1);
            setOrderTransferRate(-1);
            setOrderTransfers(-1);
        } else {
            setOrderPlacedRate(placedSessionIds.length / itemAddedToOrderSessionIds.length);
            const orderTransferedSessionIds = callTransferedSessionIds.filter(x => itemAddedToOrderSessionIds.includes(x));
            setOrderTransferRate(orderTransferedSessionIds.length / itemAddedToOrderSessionIds.length);
            setOrderTransfers(orderTransferedSessionIds?.length ?? 0);

            const conversationalSuccessSessionIds = new Set([...conversationalSuccessEvents.map(x => x.sessionId)]);
            let convSuccessSessionIds = [...conversationalSuccessSessionIds].filter(x => sessions.some(s => s.sessionId === x));
            const conversationalSuccessCount = convSuccessSessionIds.length;
            setConversationalSuccess(conversationalSuccessCount);

            const attemptedSuccessSessions = itemAddedToOrderSessionIds.filter(x => convSuccessSessionIds.includes(x));
            const attemptedConversationalSuccessRate = attemptedSuccessSessions.length / orderAttempts;
            setAttemptedOrdersConversationalSuccessRate(attemptedConversationalSuccessRate);
            setAttemptedOrderConversationalSuccess(attemptedSuccessSessions.length);

            const sessionsCount = sessions.length;
            const allConversationalSuccessRate = conversationalSuccessCount / sessionsCount;
            setConversationalSuccessRate(allConversationalSuccessRate)
        }

    }, [itemAddedToOrderEvents, orderPlacedEvents, callTransferredEvents, sessions, immediateTransferEvents, conversationalSuccessEvents, callCompleteEvents, loading]);

    useEffect(() => {
        if (initialSessions.length && callCompleteEvents.length) {
            // we don't want sessions if they had no new tts generations and 3 or less cached ones and no transfers 
            // this means they probably didn't interact at all
            const newSessions = initialSessions.filter(s => {
                const completeEvent = callCompleteEvents.find(e => e.sessionId === s.sessionId);
                if (completeEvent && (!completeEvent?.newTTSGenerations && completeEvent?.cachedTTSGenerations <= 3) || (completeEvent?.newTTSGenerations ?? 0 + (completeEvent?.cachedTTSGenerations ?? 0)) < 2) {
                    const callTransferredEvent = callTransferredEvents.find(e => e.sessionId === s.sessionId);
                    if (!callTransferredEvent) {
                        return false;
                    }
                }
                return true;
            });
            setSessions(newSessions);
            setLoading(false);
        }
    }, [initialSessions, callCompleteEvents, callTransferredEvents]);

    /**
     * Calculate healthcare-specific metrics from conversational success events (App-level)
     * This provides both English and Spanish breakdowns for all appointment types
     * 
     * TODO: Replace with API endpoint when available:
     * GET /api/analytics/app/{appId}/healthcare/metrics?startDate={date}&endDate={date}
     */
    const healthcareAppMetrics = useMemo((): HealthcareAppMetrics => {
        if (!conversationalSuccessEvents || conversationalSuccessEvents.length === 0) {
            return {
                scheduled: 0,
                scheduledEnglish: 0,
                scheduledSpanish: 0,
                rescheduled: 0,
                rescheduledEnglish: 0,
                rescheduledSpanish: 0,
                cancelled: 0,
                cancelledEnglish: 0,
                cancelledSpanish: 0,
                checkedNextAppt: 0,
                checkedNextApptEnglish: 0,
                checkedNextApptSpanish: 0,
                totalAppointmentActions: 0,
                containedCallsCount: 0,
                containedCallsPercentage: "??",
                hasSpanishCalls: false
            };
        }

        // Count scheduled appointments (ACTION_COMPLETED but not rescheduled or cancelled)
        const scheduledEnglish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            !event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        const scheduledSpanish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        // Count rescheduled appointments
        const rescheduledEnglish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            !event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        const rescheduledSpanish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        // Count cancelled appointments
        const cancelledEnglish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            !event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        const cancelledSpanish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        // Count checked next appointments
        const checkedNextApptEnglish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE) &&
            !event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        const checkedNextApptSpanish = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE) &&
            event.sessionTags?.includes(TAGS.SPANISH)
        ).length;

        const scheduled = scheduledEnglish + scheduledSpanish;
        const rescheduled = rescheduledEnglish + rescheduledSpanish;
        const cancelled = cancelledEnglish + cancelledSpanish;
        const checkedNextAppt = checkedNextApptEnglish + checkedNextApptSpanish;

        const totalAppointmentActions = scheduled + rescheduled + cancelled + checkedNextAppt;

        // Calculate contained calls
        const successfulCalls = scheduled + rescheduled + cancelled;

        const remainingContainedCalls = conversationalSuccessEvents.filter(event =>
            (event.sessionTags?.includes(TAGS.TRANSFERRED) &&
                (event.sessionTags?.includes(TAGS.SCHEDULE_ATTEMPTED) ||
                    event.sessionTags?.includes(TAGS.RESCHEDULE_ATTEMPTED) ||
                    event.sessionTags?.includes(TAGS.CANCELLATION_ATTEMPTED)) &&
                (event.sessionTags?.includes(TAGS.UNSUPPORTED_APPOINTMENT_TYPE) ||
                    event.sessionTags?.includes(TAGS.UNSUPPORTED_CUSTOMER_REQUEST))) ||
            event.sessionTags?.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE)
        ).length;

        const containedCallsCount = successfulCalls + remainingContainedCalls;

        const attemptedTotal = conversationalSuccessEvents.filter(event =>
            event.sessionCategory === "ACTION_ATTEMPTED"
        ).length;

        const containedCallsPercentage = attemptedTotal <= 0
            ? "??"
            : Math.round((containedCallsCount / attemptedTotal) * 100);

        const hasSpanishCalls = conversationalSuccessEvents.some(event =>
            event.sessionTags?.includes(TAGS.SPANISH)
        );

        return {
            scheduled,
            scheduledEnglish,
            scheduledSpanish,
            rescheduled,
            rescheduledEnglish,
            rescheduledSpanish,
            cancelled,
            cancelledEnglish,
            cancelledSpanish,
            checkedNextAppt,
            checkedNextApptEnglish,
            checkedNextApptSpanish,
            totalAppointmentActions,
            containedCallsCount,
            containedCallsPercentage,
            hasSpanishCalls
        };
    }, [conversationalSuccessEvents]);

    /**
     * Calculate organization-level healthcare metrics from conversational success events
     * Aggregates data across multiple healthcare applications
     * 
     * TODO: Replace with API endpoint when available:
     * GET /api/analytics/org/{orgId}/healthcare/metrics?startDate={date}&endDate={date}
     * 
     * @param aggregatedSessions - Sessions from all healthcare apps in the org
     * @param aggregatedCallCompleteEvents - Call complete events from all healthcare apps
     * @param healthcareApps - Array of healthcare application objects
     */
    const calculateHealthcareOrgMetrics = (
        aggregatedSessions: any[],
        aggregatedCallCompleteEvents: any[],
        healthcareApps?: any[]
    ): HealthcareOrgMetrics => {
        // Start with app-level metrics as base
        const baseMetrics = healthcareAppMetrics;

        if (!conversationalSuccessEvents || conversationalSuccessEvents.length === 0) {
            return {
                ...baseMetrics,
                totalEngagements: aggregatedSessions?.length || 0,
                offHoursCalls: 0,
                transferCount: 0,
                faqCount: 0,
                averageCallLength: 0,
                averageContainedCallLength: 0,
                averageCallLengthByCategory: {
                    scheduled: 0,
                    rescheduled: 0,
                    cancelled: 0,
                    checkedNextAppt: 0
                },
                laborHoursSaved: 0,
                callVolumeByHour: [],
                appointmentsByApp: [],
                containedCallsByApp: [],
                newPatientBookings: 0,
                existingPatientBookings: 0,
                totalBookings: 0,
                newPatientBookingsByApp: []
            };
        }

        // Count transfer and FAQ events
        let transferCount = 0;
        let faqCount = 0;

        conversationalSuccessEvents.forEach(event => {
            const tags = event.sessionTags || [];
            const category = event.sessionCategory;

            if (tags.includes(TAGS.TRANSFERRED)) {
                transferCount++;
            }

            if (category === 'QUESTION_ANSWERED') {
                faqCount++;
            }
        });

        const totalSessions = aggregatedSessions?.length || 1;
        const transferredSessionIds = new Set<string>();

        conversationalSuccessEvents.forEach(event => {
            const tags = event.sessionTags || [];
            if (tags.includes(TAGS.TRANSFERRED) && event.sessionId) {
                transferredSessionIds.add(event.sessionId);
            }
        });

        // Calculate average call lengths from call complete events
        let totalDuration = 0;
        let containedCallDuration = 0;
        let containedCallCount = 0;
        const callCompleteCount = aggregatedCallCompleteEvents?.length || 0;

        aggregatedCallCompleteEvents?.forEach(event => {
            const duration = event.callDuration || event.duration || 0;
            totalDuration += duration;

            const wasContained = !transferredSessionIds.has(event.sessionId);
            if (wasContained) {
                containedCallDuration += duration;
                containedCallCount++;
            }
        });

        const averageCallLength = callCompleteCount > 0
            ? totalDuration / callCompleteCount / 60 // Convert to minutes
            : 0;

        const averageContainedCallLength = containedCallCount > 0
            ? containedCallDuration / containedCallCount / 60 // Convert to minutes
            : 0;

        // Calculate average call length by category
        const sessionCategoryMap = new Map<string, string[]>();
        conversationalSuccessEvents.forEach(event => {
            if (event.sessionId) {
                const tags = event.sessionTags || [];
                const categories: string[] = [];

                if (tags.includes(TAGS.ACTION_COMPLETED) &&
                    !tags.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                    !tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                    categories.push('scheduled');
                }
                if (tags.includes(TAGS.APPOINTMENT_RESCHEDULED)) {
                    categories.push('rescheduled');
                }
                if (tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                    categories.push('cancelled');
                }
                if (tags.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE)) {
                    categories.push('checkedNextAppt');
                }

                if (categories.length > 0) {
                    sessionCategoryMap.set(event.sessionId, categories);
                }
            }
        });

        const categoryDurations = {
            scheduled: { total: 0, count: 0 },
            rescheduled: { total: 0, count: 0 },
            cancelled: { total: 0, count: 0 },
            checkedNextAppt: { total: 0, count: 0 }
        };

        aggregatedCallCompleteEvents?.forEach(event => {
            const duration = event.callDuration || event.duration || 0;
            const categories = sessionCategoryMap.get(event.sessionId);

            if (categories) {
                categories.forEach(category => {
                    if (categoryDurations[category]) {
                        categoryDurations[category].total += duration;
                        categoryDurations[category].count++;
                    }
                });
            }
        });

        const averageCallLengthByCategory: AverageCallLengthByCategory = {
            scheduled: categoryDurations.scheduled.count > 0
                ? categoryDurations.scheduled.total / categoryDurations.scheduled.count / 60
                : 0,
            rescheduled: categoryDurations.rescheduled.count > 0
                ? categoryDurations.rescheduled.total / categoryDurations.rescheduled.count / 60
                : 0,
            cancelled: categoryDurations.cancelled.count > 0
                ? categoryDurations.cancelled.total / categoryDurations.cancelled.count / 60
                : 0,
            checkedNextAppt: categoryDurations.checkedNextAppt.count > 0
                ? categoryDurations.checkedNextAppt.total / categoryDurations.checkedNextAppt.count / 60
                : 0
        };

        // Calculate labor hours saved
        const orgContainedCallsCount = totalSessions - transferredSessionIds.size;
        const laborHoursSaved = orgContainedCallsCount > 0 && averageContainedCallLength > 0
            ? (orgContainedCallsCount * averageContainedCallLength) / 60 // Convert minutes to hours
            : 0;

        // Calculate call volume by hour with app breakdown
        const hourData: { [key: string]: { total: number; byApp: { [appId: string]: { appName: string; count: number } } } } = {};

        // Initialize all hours from 8 AM to 5 PM
        for (let hour = 8; hour <= 17; hour++) {
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour > 12 ? hour - 12 : hour;
            const label = `${displayHour} ${period}`;
            hourData[label] = { total: 0, byApp: {} };
        }

        // Count sessions by hour across all apps
        aggregatedSessions?.forEach(session => {
            const startDate = new Date(session.startDate || session.date);
            const hour = startDate.getHours();

            if (hour >= 8 && hour <= 17) {
                const period = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour > 12 ? hour - 12 : hour;
                const label = `${displayHour} ${period}`;
                hourData[label].total++;

                const appId = session.applicationId;
                if (appId) {
                    const app = healthcareApps?.find(a => a.id === appId);
                    const appName = app?.name || 'Unknown';
                    if (!hourData[label].byApp[appId]) {
                        hourData[label].byApp[appId] = { appName, count: 0 };
                    }
                    hourData[label].byApp[appId].count++;
                }
            }
        });

        const callVolumeByHour: CallVolumeByHour[] = Object.entries(hourData).map(([hour, data]) => ({
            hour,
            calls: data.total,
            apps: Object.values(data.byApp).sort((a, b) => b.count - a.count)
        }));

        // Calculate appointments by app
        const appointmentsByApp: AppointmentByApp[] = [];

        if (healthcareApps && healthcareApps.length > 0) {
            healthcareApps.forEach(app => {
                const appEvents = conversationalSuccessEvents.filter(event => event.applicationId === app.id);

                let appScheduled = 0;
                let appRescheduled = 0;
                let appCancelled = 0;
                let appCheckedNextAppt = 0;

                appEvents.forEach(event => {
                    const tags = event.sessionTags || [];

                    if (tags.includes(TAGS.ACTION_COMPLETED) &&
                        !tags.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                        !tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                        appScheduled++;
                    }

                    if (tags.includes(TAGS.APPOINTMENT_RESCHEDULED)) {
                        appRescheduled++;
                    }

                    if (tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                        appCancelled++;
                    }

                    if (tags.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE)) {
                        appCheckedNextAppt++;
                    }
                });

                if (appScheduled + appRescheduled + appCancelled + appCheckedNextAppt > 0) {
                    appointmentsByApp.push({
                        appId: app.id,
                        appName: app.name || 'Unknown App',
                        scheduled: appScheduled,
                        rescheduled: appRescheduled,
                        cancelled: appCancelled,
                        checkedNextAppt: appCheckedNextAppt
                    });
                }
            });
        }

        // Calculate contained calls breakdown by app
        const containedCallsByApp: ContainedCallsByApp[] = [];

        if (healthcareApps && healthcareApps.length > 0) {
            healthcareApps.forEach(app => {
                const appEvents = conversationalSuccessEvents.filter(event => event.applicationId === app.id);

                let appScheduled = 0;
                let appScheduledEnglish = 0;
                let appScheduledSpanish = 0;
                let appRescheduled = 0;
                let appRescheduledEnglish = 0;
                let appRescheduledSpanish = 0;
                let appCancelled = 0;
                let appCancelledEnglish = 0;
                let appCancelledSpanish = 0;
                let appCheckedNextAppt = 0;
                let appCheckedNextApptEnglish = 0;
                let appCheckedNextApptSpanish = 0;
                let appFaq = 0;
                let appFaqEnglish = 0;
                let appFaqSpanish = 0;
                let appTransfer = 0;
                let appTransferEnglish = 0;
                let appTransferSpanish = 0;

                appEvents.forEach(event => {
                    const tags = event.sessionTags || [];
                    const category = event.sessionCategory;
                    const isSpanish = tags.includes(TAGS.SPANISH);

                    if (tags.includes(TAGS.ACTION_COMPLETED) &&
                        !tags.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                        !tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                        appScheduled++;
                        if (isSpanish) {
                            appScheduledSpanish++;
                        } else {
                            appScheduledEnglish++;
                        }
                    }

                    if (tags.includes(TAGS.APPOINTMENT_RESCHEDULED)) {
                        appRescheduled++;
                        if (isSpanish) {
                            appRescheduledSpanish++;
                        } else {
                            appRescheduledEnglish++;
                        }
                    }

                    if (tags.includes(TAGS.APPOINTMENT_CANCELLED)) {
                        appCancelled++;
                        if (isSpanish) {
                            appCancelledSpanish++;
                        } else {
                            appCancelledEnglish++;
                        }
                    }

                    if (tags.includes(TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE)) {
                        appCheckedNextAppt++;
                        if (isSpanish) {
                            appCheckedNextApptSpanish++;
                        } else {
                            appCheckedNextApptEnglish++;
                        }
                    }

                    if (tags.includes(TAGS.TRANSFERRED)) {
                        appTransfer++;
                        if (isSpanish) {
                            appTransferSpanish++;
                        } else {
                            appTransferEnglish++;
                        }
                    }

                    if (category === 'QUESTION_ANSWERED') {
                        appFaq++;
                        if (isSpanish) {
                            appFaqSpanish++;
                        } else {
                            appFaqEnglish++;
                        }
                    }
                });

                const totalActions = appScheduled + appRescheduled + appCancelled + appCheckedNextAppt + appFaq + appTransfer;
                if (totalActions > 0) {
                    containedCallsByApp.push({
                        appId: app.id,
                        appName: app.name || 'Unknown App',
                        scheduled: appScheduled,
                        scheduledEnglish: appScheduledEnglish,
                        scheduledSpanish: appScheduledSpanish,
                        rescheduled: appRescheduled,
                        rescheduledEnglish: appRescheduledEnglish,
                        rescheduledSpanish: appRescheduledSpanish,
                        cancelled: appCancelled,
                        cancelledEnglish: appCancelledEnglish,
                        cancelledSpanish: appCancelledSpanish,
                        checkedNextAppt: appCheckedNextAppt,
                        checkedNextApptEnglish: appCheckedNextApptEnglish,
                        checkedNextApptSpanish: appCheckedNextApptSpanish,
                        faq: appFaq,
                        faqEnglish: appFaqEnglish,
                        faqSpanish: appFaqSpanish,
                        transfer: appTransfer,
                        transferEnglish: appTransferEnglish,
                        transferSpanish: appTransferSpanish
                    });
                }
            });
        }

        // Calculate new patient bookings
        // TODO: Add API endpoint: GET /api/analytics/org/{orgId}/healthcare/new-patient-events?startDate={date}&endDate={date}
        // This endpoint should return conversational success events filtered by the "NEW_PATIENT" tag
        const newPatientBookings = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            event.sessionTags?.includes(TAGS.NEW_PATIENT)
        ).length;

        const existingPatientBookings = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
            !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
            !event.sessionTags?.includes(TAGS.NEW_PATIENT)
        ).length;

        const totalBookings = newPatientBookings + existingPatientBookings;

        // Calculate off hours calls
        const offHoursCalls = conversationalSuccessEvents.filter(event =>
            event.sessionTags?.includes(TAGS.CALL_DURING_OFF_HOURS)
        ).length;

        // Calculate new patient bookings by app
        const newPatientBookingsByApp: NewPatientBookingsByApp[] = [];

        if (healthcareApps && healthcareApps.length > 0) {
            healthcareApps.forEach(app => {
                const appEvents = conversationalSuccessEvents.filter(event => event.applicationId === app.id);

                const newPatient = appEvents.filter(event =>
                    event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
                    !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                    !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
                    event.sessionTags?.includes(TAGS.NEW_PATIENT)
                ).length;

                const existingPatient = appEvents.filter(event =>
                    event.sessionTags?.includes(TAGS.ACTION_COMPLETED) &&
                    !event.sessionTags?.includes(TAGS.APPOINTMENT_RESCHEDULED) &&
                    !event.sessionTags?.includes(TAGS.APPOINTMENT_CANCELLED) &&
                    !event.sessionTags?.includes(TAGS.NEW_PATIENT)
                ).length;

                if (newPatient > 0 || existingPatient > 0) {
                    newPatientBookingsByApp.push({
                        appId: app.id,
                        appName: app.name,
                        newPatient,
                        existingPatient
                    });
                }
            });
        }

        return {
            ...baseMetrics,
            totalEngagements: totalSessions,
            offHoursCalls,
            transferCount,
            faqCount,
            averageCallLength,
            averageContainedCallLength,
            averageCallLengthByCategory,
            laborHoursSaved,
            callVolumeByHour,
            appointmentsByApp,
            containedCallsByApp,
            newPatientBookings,
            existingPatientBookings,
            totalBookings,
            newPatientBookingsByApp
        };
    };

    /**
     * Calculate food ordering metrics from order placed events and call complete events (App-level)
     * 
     * TODO: Replace with API endpoint when available:
     * GET /api/analytics/app/{appId}/ordering/metrics?startDate={date}&endDate={date}
     */
    const foodOrderingAppMetrics = useMemo((): FoodOrderingAppMetrics => {
        if (!orderPlacedEvents || orderPlacedEvents.length === 0 || !callCompleteEvents || callCompleteEvents.length === 0) {
            return {
                totalSold: 0,
                averageOrderLength: 0,
                convertedCustomers: -1,
                repeatCustomers: -1,
                uniqueEngagementCount: -1,
                upsellPercentage: -1
            };
        }

        // Calculate total sold
        const totalSold = orderPlacedEvents.reduce((acc, event) => acc + (event.total || 0), 0);

        // Get all call complete events that have a sessionId in the orderPlacedEvents
        const orderPlacedCompleteEvents = callCompleteEvents.filter(event =>
            orderPlacedEvents.some(orderEvent => orderEvent.sessionId === event.sessionId)
        );

        // Calculate average order length
        const totalDuration = orderPlacedCompleteEvents.reduce((acc, event) => acc + (event.duration || 0), 0);
        const averageOrderLength = totalDuration / (orderPlacedCompleteEvents.length || 1);

        return {
            totalSold,
            averageOrderLength,
            convertedCustomers: -1, // These are calculated in useProcessConversationalSuccessEvents hook
            repeatCustomers: -1,
            uniqueEngagementCount: -1,
            upsellPercentage: -1
        };
    }, [orderPlacedEvents, callCompleteEvents]);

    /**
     * Calculate labor hours saved
     * For healthcare: based on ACTION_COMPLETED call durations
     * For food ordering: based on average order length * number of orders
     */
    const calculateLaborSavings = (
        sessionsForReview: any[],
        processedEvents: any[],
        industryType: 'healthcare' | 'foodOrdering'
    ): LaborSavingsMetrics => {
        if (industryType === 'healthcare') {
            const actionCompletedEvents = processedEvents?.filter(event =>
                event.tags?.includes(TAGS.ACTION_COMPLETED)
            );

            const totalDuration = actionCompletedEvents?.reduce((acc, event) => {
                const idCandidates = [event.sessionId].concat(event?.mergedSessions?.map(ms => ms.sessionId) || []);
                const completedSessionId = idCandidates.find(id => {
                    const convEvent = conversationalSuccessEvents?.filter(e => e.sessionId === id);
                    if (convEvent[0]?.sessionTags?.includes(TAGS.ACTION_COMPLETED)) {
                        return id;
                    }
                });

                if (completedSessionId !== null && completedSessionId !== undefined) {
                    const sessionForReview = sessionsForReview.find(s => s.sessionId === completedSessionId);
                    if (sessionForReview) {
                        return acc + (sessionForReview.duration || 0);
                    }
                }
                return acc;
            }, 0) || 0;

            return {
                hoursSaved: Math.round((totalDuration / 3600) * 100) / 100,
                totalDurationSeconds: totalDuration
            };
        } else {
            // Food ordering
            const avgOrder = foodOrderingAppMetrics.averageOrderLength;
            const secondsSaved = avgOrder * (orderPlacedEvents?.length || 0);
            return {
                hoursSaved: Math.round((secondsSaved / 3600) * 100) / 100,
                totalDurationSeconds: secondsSaved
            };
        }
    };

    const getUniqueSessionIds = (events: any[]) => {
        const grouped = events.reduce((acc, item) => {
            if (!acc[item.sessionId]) {
                acc[item.sessionId] = [];
            }
            acc[item.sessionId].push(item);
            return acc;
        }, {});
        return Object.keys(grouped);
    };

    const loadAllEventsForSession = async (sessionId: string, applicationId: string) => {
        if (sessionId in allEventsCache) {
            return allEventsCache[sessionId];
        } else {
            setLoadingSessionId(sessionId);
            const allEvents = await voicifyApi.getAllEventsForSession(applicationId, sessionId);
            if (allEvents.resultType === "Ok") {
                allEvents.data.forEach(e => {
                    if (e.eventDate) {
                        e.timestamp = new Date(e.eventDate).getTime()
                    }
                })
                // sort by timestampt
                allEvents.data.sort((a, b) => a.timestamp - b.timestamp);
                const newEventsCache = { ...allEventsCache };
                newEventsCache[sessionId] = allEvents.data;
                setAllEventsCache(newEventsCache);
            }
            setLoadingSessionId("");
            return allEvents;
        }
    };


    const loadOrderingAnalytics = async (applicationId: string, filter: AnalyticsRequestFilter) => {
        setLoading(true);
        setInitialSessions([]);
        setItemAddedToOrderEvents([]);
        setOrderPlacedEvents([]);
        setCallTransferredEvents([]);
        setImmediateTransferEvents([]);
        setConversationalSuccessEvents([]);
        setCallCompleteEvents([]);
        const tasks = []
        tasks.push(voicifyApi.getSessionsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getItemAddedToOrderEventsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getOrderPlacedEventsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getCallTransferredEventsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getImmediateTransferEventsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getConversationalSuccessEventsByApplication(applicationId, filter));
        tasks.push(voicifyApi.getCallCompleteEventsByApplication(applicationId, filter));
        // wait for all tasks
        const results = await Promise.all(tasks);
        let errors = [];
        const errorResults = results.filter(x => x.resultType !== "Ok");
        if (errorResults.length) {
            errors = [...errors, ...errorResults.map(x => x.errors[0])];
            setErrors(errors);
            setLoading(false);
            return;
        }
        setInitialSessions(results[0].data);
        setItemAddedToOrderEvents(results[1].data);
        setOrderPlacedEvents(results[2].data);
        setCallTransferredEvents(results[3].data);
        setImmediateTransferEvents(results[4].data);
        setConversationalSuccessEvents(results[5].data);
        setCallCompleteEvents(results[6].data);
    };

    const loadOrderPlacedEventsPaged = async (applicationId: string, filter: AnalyticsRequestFilter) => {
        let skip = 0;
        let take = 500;
        let allEvents = [];
        let hasMore = true;
        while (hasMore) {
            const result = await voicifyApi.getOrderPlacedEventsByApplication(applicationId, {
                ...filter,
                skip,
                take
            });
            if (result.resultType === "Ok") {
                allEvents = [...allEvents, ...result.data];
                hasMore = result.data.length === take;
                skip += take;
            } else {
                return result;
                hasMore = false;
            }
        }
        setOrderPlacedEvents(allEvents);
        return {
            resultType: "Ok",
            data: allEvents,
            errors: null
        };
    }

    const loadCallCompleteEventsPaged = async (applicationId: string, filter: AnalyticsRequestFilter) => {
        let skip = 0;
        let take = 500;
        let allEvents = [];
        let hasMore = true;
        while (hasMore) {
            const result = await voicifyApi.getCallCompleteEventsByApplication(applicationId, {
                ...filter,
                skip,
                take
            });
            if (result.resultType === "Ok") {
                allEvents = [...allEvents, ...result.data];
                hasMore = result.data.length === take;
                skip += take;
            } else {
                return result;
                hasMore = false;
            }
        }
        setCallCompleteEvents(allEvents);
        return {
            resultType: "Ok",
            data: allEvents,
            errors: null
        };
    }

    const loadConversationalSuccessEventsPaged = async (applicationId: string, filter: AnalyticsRequestFilter) => {
        let skip = 0;
        let take = 500;
        let allEvents = [];
        let hasMore = true;
        while (hasMore) {
            const result = await voicifyApi.getConversationalSuccessEventsByApplication(applicationId, {
                ...filter,
                skip,
                take
            });
            if (result.resultType === "Ok") {
                allEvents = [...allEvents, ...result.data];
                hasMore = result.data.length === take;
                skip += take;
            } else {
                return result;
                hasMore = false;
            }
        }
        return {
            resultType: "Ok",
            data: allEvents,
            errors: null
        };
    }
    const loadConversationalSuccessEvents = async (applicationId: string, filter: AnalyticsRequestFilter) => {
        setLoading(true);
        setErrors([]);
        setConversationalSuccessEvents([]);
        const result = await loadConversationalSuccessEventsPaged(applicationId, filter);
        if (result.resultType === "Ok") {
            setConversationalSuccessEvents(result.data);
        } else {
            setErrors((prev) => [...prev, result?.errors[0]]);
        }
        setLoading(false);
    };

    const loadConversationalSuccessEventsForOrgs = async (orgIds: string[], filter: AnalyticsRequestFilter, logCallback?: (message: string) => void) => {
        setLoading(true);
        setErrors([]);
        setConversationalSuccessEvents([]);
        let allEvents = [];
        const loadErrors = [];

        for (let i = 0; i < orgIds.length; i++) {
            const orgId = orgIds[i];
            try {
                logCallback?.(`Loading success events for organization ${i + 1} of ${orgIds.length}...`);
                const result = await loadConversationalSuccessEventsPagedForOrg(orgId, filter);
                if (result.resultType === "Ok") {
                    allEvents = [...allEvents, ...result.data];
                    logCallback?.(`Loaded ${result.data.length} success events for organization ${i + 1}`);
                } else {
                    const errorMsg = `Failed to load events for org ${orgId}: ${result?.errors?.[0] || 'Unknown error'}`;
                    loadErrors.push(errorMsg);
                    logCallback?.(errorMsg);
                }
            } catch (error) {
                const errorMsg = `Failed to load events for org ${orgId}: ${error.message || 'Unknown error'}`;
                loadErrors.push(errorMsg);
                logCallback?.(errorMsg);
            }
        }

        if (loadErrors.length > 0) {
            setErrors(loadErrors);
        }

        setConversationalSuccessEvents(allEvents);
        setLoading(false);

        // Return the loaded events
        return allEvents;
    };

    const loadConversationalSuccessEventsPagedForOrg = async (orgId: string, filter: AnalyticsRequestFilter) => {
        let skip = 0;
        let take = 200;
        let allEvents = [];
        let hasMore = true;

        while (hasMore) {
            const result = await voicifyApi.getConversationalSuccessEvents(orgId, {
                ...filter,
                skip,
                take
            });

            if (result.resultType === "Ok") {
                allEvents = [...allEvents, ...result.data];
                hasMore = result.data.length === take;
                skip += take;
            } else {
                return result;
            }
        }

        return {
            resultType: "Ok",
            data: allEvents,
            errors: null
        };
    };

    return {
        loading,
        setLoading,
        errors,
        unsuccessfulOrderSessionIds,
        orderAttempts,
        ordersPlaced,
        sessions,
        orderPlacedRate,
        orderTransferRate,
        transferRate,
        transfers,
        orderTransfers,
        allEventsCache,
        noOrderSessionIds,
        immediateTransfers,
        immediateTransferRate,
        loadingSessionId,
        ordersPlacedSessionIds,
        orderPlacedEvents,
        orderTransferredSessionIds,
        conversationalSuccessRate,
        attemptedOrdersConversationalSuccessRate,
        loadOrderingAnalytics,
        loadAllEventsForSession,
        loadConversationalSuccessEvents,
        loadConversationalSuccessEventsForOrgs,
        loadOrderPlacedEventsPaged,
        loadCallCompleteEventsPaged,
        loadConversationalSuccessEventsPaged,
        callCompleteEvents,
        conversationalSuccessEvents,
        conversationalSuccess,
        attemptedOrderConversationalSuccess,
        // New calculated metrics - App level
        healthcareAppMetrics,
        foodOrderingAppMetrics,
        calculateLaborSavings,
        // Org level calculation functions
        calculateHealthcareOrgMetrics
    };
}

const TelephonyAnalyticsContainer = createContainer(useTelephonyAnalyticsContainer);
export default TelephonyAnalyticsContainer;
